import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '../../../../utils/config-service';

import { Job, CreateJobDto } from '../../models/job';
import { Observable } from 'rxjs/Observable';
import { Project } from '../../models/project';

@Injectable()
export class JobApiService {
  private jobUrl: string;

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.jobUrl = this.configService.getEnv('jobApi');
  }

  getAllJobs(): Observable<{} | Job[]> {
    return this.http.get(this.jobUrl)
      .map((result: any) => {
        let json = result;
        return json.map((jobJson: any) => new Job(jobJson));
      }).catch((error: any) => {
        throw error;
      });
  }

  getAllUnfilteredJobs(): Observable<{} | Job[]> {
    return this.http.get(this.jobUrl + 'All')
      .map((result: any) => {
        let json = result;
        return json.map((jobJson: any) => new Job(jobJson));
      }).catch((error: any) => {
        throw error;
      });
  }

  getAllJobsInProjects(): Observable<{} | Job[]> {
    let url = this.jobUrl + 'project/jobs';
    return this.http.get(url)
      .map((result: any) => {
        let json = result;
        return json.map((jobJson: any) => new Job(jobJson));
      }).catch((error: any) => {
        throw error;
      });
  }

  getArchivedJobs(): Observable<{} | Job[]> {
    let archivedUrl = this.jobUrl + 'Archive';
    return this.http.get(archivedUrl)
      .map((result: any) => {
        let json = result;
        return json.map((jobJson: any) => new Job(jobJson));
      }).catch((error: any) => {
        throw error;
      });
  }

  getJob(jobId: string): Observable<void | Job> {
    return this.http.get(this.jobUrl + jobId)
      .map((result: any) => {
        return new Job(result);
      }, (error: any) => {
        console.log(error);
      });
  }

  getJobStatus(jobId: string): Observable<{} | string> {
    let url = this.jobUrl + jobId + '/status';
    return this.http.get(url)
      .map((result: any) => {
        return result.status;
      }).catch((error: any) => {
        return undefined;
      });
  }

  createJob(jobDto: Job): Observable<any> {
    let createJobDto = new CreateJobDto(jobDto);
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(this.jobUrl, JSON.stringify(createJobDto), { headers: headers })
      .map((result: any) => {
        return result.jobId;
      }).catch((error: any) => {
        return undefined;
      });
  }

  archiveJob(jobId: string, archive: boolean): Observable<any> {
    let url = this.jobUrl + jobId + '/Archive/' + archive;
    return this.http.put(url, null)
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        return error;
      });
  }

  shareJob(jobId: string, share: boolean): Observable<any> {
    let url = this.jobUrl + jobId + '/Share/' + share;
    return this.http.put(url, null, { 'responseType': 'text' })
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        return error;
      });
  }

  shareProject(projectId: string, share: boolean): Observable<string> {
    let url = this.jobUrl + projectId + '/project/Share/' + share;
    return this.http.put(url, null, { 'responseType': 'text' })
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        return error;
      });
  }


  deleteJob(jobId: string): Observable<any> {
    return this.http.delete(this.jobUrl + jobId)
      .map((result: any) => {
        console.log(result);
      }).catch((error: any) => {
        return error;
      });
  }

  stopJob(jobId: string): Observable<any> {
    let stopUrl = this.jobUrl + jobId + '/stop';
    return this.http.put(stopUrl, null)
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        throw error;
      });
  }

  getProjects(): Observable<{} | Project[]> {
    let url = this.jobUrl + 'project';
    return this.http.get(url)
      .map((result: any) => {
        return result.map((json: any) => new Project(json.project, json.jobs));
      }).catch((error: any) => {
        throw error;
      });
  }

  getProjectById(projectId: string): Observable<{} | Project> {
    let url = this.jobUrl + `${projectId}/project`;
    return this.http.get(url)
      .map((result: any) => {
        return new Project(result.project, result.jobs);
      }).catch((error: any) => {
        throw error;
      });
  }

  createProject(project: Project): Observable<{} | any> {
    let url = this.jobUrl + 'project';
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(url, JSON.stringify(project), { headers: headers })
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        return undefined;
      });
  }

  deleteProject(projectId: string): Observable<{} | any> {
    let url = this.jobUrl + `${projectId}/project`;
    return this.http.delete(url)
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        throw error;
      });
  }

  getArchivedProjects(): Observable<{} | Project[]> {
    let archivedUrl = this.jobUrl + 'Project/Archive';
    return this.http.get(archivedUrl)
      .map((result: any) => {
        return result.map((json: any) => new Project(json.project, json.jobs));
      }).catch((error: any) => {
        throw error;
      });
  }

  archiveProject(projectId: string, archive: boolean): Observable<any> {
    let url = this.jobUrl + projectId + '/Project/Archive/' + archive;
    return this.http.put(url, null)
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        return error;
      });
  }

  addJobToProject(jobId: string, projectId: string): Observable<any> {
    let url = this.jobUrl + `${jobId}/Project/`;
    if (projectId !== null) {
      url += projectId;
    }
    return this.http.put(url, null)
      .map((result: any) => {
        return result;
      }).catch((error: any) => {
        throw error;
      });
  }

  getTimezoneForJob(jobId: string): Observable<{} | string> {
    let url = this.jobUrl + `timezone/${jobId}`;
    return this.http.get(url)
      .map((result: any) => {
        return result.timeZoneId;
      })
      .catch((error: any) => {
        throw error;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/domain/services/job/job.api.service.ts